// src/websocket/subscription.ts
import { EventEmitter } from 'eventemitter3';
import { subscribe_to_channel, unsubscribe_from_channel } from './helper_functions';
export class BaseSubscription extends EventEmitter {
    constructor(connectionID, channelConfig, websocketHandler, process = "subscribe") {
        super();
        this.isSubscribed = false;
        this.isListening = false;
        this.messageBuffer = new Map();
        this.threadBuffers = new Map();
        this.pendingAcks = new Map();
        this.ACK_TIMEOUT = 50000;
        this.messageCount = 0;
        this.presenceUsers = [];
        this.connectionID = connectionID;
        this.websocketHandler = websocketHandler;
        this.channelConfig = channelConfig;
        this.presenceUsers = this.channelConfig.presenceUsers;
        if (process === "subscribe") {
            this.isSubscribed = true;
        }
        else if (process === "presence") {
            this.isListening = true;
        }
    }
    async validateSubscription(process) {
        try {
            if (["art_config", "art_secure"].includes(this.channelConfig.channelName)) {
                return;
            }
            let channelName = this.channelConfig.channelName;
            if (this.channelConfig.channelNamespace) {
                channelName += ":" + this.channelConfig.channelNamespace;
            }
            await subscribe_to_channel(channelName, process, this.websocketHandler).then((config) => {
                this.channelConfig = config;
                if (process === "presence") {
                    this.isListening = true;
                }
            }).catch((error) => {
                console.error(`${error}`);
            });
        }
        catch (error) {
            console.error(`${error}`);
        }
    }
    /**
     * Check for presence on the channel.
     * @param callback Callback to handle the presence data.
     * @throws Error if not subscribed for presence.
     */
    async fetchPresence(callback, options = { unique: true }) {
        var _a;
        let previousPresenceData = (_a = this.presenceUsers) !== null && _a !== void 0 ? _a : [];
        if (previousPresenceData.length > 0) {
            callback(previousPresenceData);
        }
        await this.validateSubscription("presence");
        if (!this.isListening) {
            throw new Error("Not subscribed for presence");
        }
        this.on('art_presence', ({ usernames, error }) => {
            if (!error) {
                previousPresenceData = usernames;
                this.presenceUsers = usernames;
                // ['b:69a1332c76bb479a0aefc86a', 'a:69a132f8c3a9b48f542d4b26', 'a:69a1331876bb479a0aefc869']
                let user_response = [];
                if (options === null || options === void 0 ? void 0 : options.unique) {
                    usernames.forEach((user) => {
                        let [user_name, _connection_id] = user.split(":");
                        if (user_response.indexOf(user_name) < 0) {
                            user_response.push(user_name);
                        }
                    });
                }
                else {
                    user_response = usernames;
                }
                callback(user_response);
            }
        });
        this.push('art_presence', {});
        return {
            unsubscribe: async () => {
                var _a;
                await unsubscribe_from_channel(this.channelConfig.channelName, (_a = this.channelConfig.subscriptionID) !== null && _a !== void 0 ? _a : "", "presence", this.websocketHandler).then((success) => {
                    if (!success) {
                        console.error(`Failed to unsubscribe from channel ${this.channelConfig.channelName}`);
                    }
                });
            }
        };
    }
    acknowledge(request, res) {
        if (this.channelConfig.channelType !== "targeted" && this.channelConfig.channelType !== "secure") {
            return;
        }
        const { id, from, channel, namespace, to, pipeline_id, attempt_id, interceptor_name, to_username, ref_id } = request;
        if (["art_config", "art_secure", "art_presence"].includes(channel)) {
            return;
        }
        const response = {
            channel: channel,
            namespace: namespace,
            id: id,
            ref_id,
            from,
            to_username,
            to,
            return_flag: res,
            pipeline_id: pipeline_id,
            interceptor_name: interceptor_name,
            attempt_id: attempt_id,
        };
        this.websocketHandler.sendMessage(JSON.stringify(response));
    }
    async handleMessageAcks(event, return_flag, data) {
        if (return_flag === 'SA' && data.ref_id) {
            const entry = this.pendingAcks.get(data.ref_id);
            if (entry) {
                clearTimeout(entry.timer);
                entry.resolve(data.ref_id);
                this.pendingAcks.delete(data.ref_id);
                return;
            }
        }
    }
    async subscribe() {
        if (["art_config", "art_secure"].includes(this.channelConfig.channelName)) {
            return async () => { };
        }
        this.isSubscribed = true;
        await subscribe_to_channel(this.channelConfig.channelName, "subscribe", this.websocketHandler).then((config) => {
            this.channelConfig = config;
        }).catch((error) => {
            console.error(`${error}`);
            this.isSubscribed = false;
        });
    }
    async unsubscribe() {
        var _a;
        if (!this.channelConfig.subscriptionID) {
            return;
        }
        await unsubscribe_from_channel(this.channelConfig.channelName, (_a = this.channelConfig.subscriptionID) !== null && _a !== void 0 ? _a : "", "subscribe", this.websocketHandler).then((success) => {
            if (!success) {
                console.error(`Failed to unsubscribe from channel ${this.channelConfig.channelName}`);
            }
            else {
                this.websocketHandler.removeSubscription(this.channelConfig.channelName);
            }
        });
    }
    reconnect() {
        if (this.channelConfig.channelName !== "art_config" && this.channelConfig.channelName !== "art_secure") {
            if (this.isListening) {
                this.validateSubscription("presence");
            }
            this.subscribe();
        }
    }
    /**
     * Push a message to the channel with a specific event and data.
     * @param event The event name.
     * @param data The data to send.
     */
    async push(event, data, options) {
        var _a, _b, _c, _d;
        await this.websocketHandler.wait();
        const connection = this.websocketHandler.getConnection();
        let to = (_a = options === null || options === void 0 ? void 0 : options.to) !== null && _a !== void 0 ? _a : [];
        let thread_id = (_b = options === null || options === void 0 ? void 0 : options.ThreadID) !== null && _b !== void 0 ? _b : null;
        let messageStr = JSON.stringify(data);
        if (this.channelConfig.channelType == "secure" || this.channelConfig.channelType == "targeted") {
            if ((!to || to.length !== 1) && event !== 'art_presence') {
                throw new Error("Exactly one user must be specified for sending message.");
            }
        }
        if (this.channelConfig.channelType == "secure" && event !== 'art_presence') {
            const { data } = await this.websocketHandler.pushForSecureLine("secured_public_key", {
                "username": to[0]
            }, true);
            if (data['status'] == 'unsuccessfull') {
                throw Error(data['error']);
            }
            messageStr = await this.websocketHandler.encrypt(messageStr, data['public_key']);
        }
        let refId;
        let ackPromise = Promise.resolve(undefined);
        if (!["art_config", "art_secure", "art_presence"].includes(this.channelConfig.channelName)) {
            this.messageCount++;
            refId = `${(_c = this.websocketHandler.getConnection()) === null || _c === void 0 ? void 0 : _c.ConnectionId}_${this.channelConfig.channelName}_${this.messageCount}`;
            ackPromise = new Promise((resolve, reject) => {
                const timer = setTimeout(() => {
                    this.pendingAcks.delete(refId);
                    reject(new Error('ACK timeout'));
                }, this.ACK_TIMEOUT);
                if (this.channelConfig.channelType === "targeted" || this.channelConfig.channelName === "secure") {
                    this.pendingAcks.set(refId, { resolve, reject, timer });
                }
                else {
                    resolve(refId);
                }
            });
        }
        const message = {
            from: (_d = connection === null || connection === void 0 ? void 0 : connection.ConnectionId) !== null && _d !== void 0 ? _d : "",
            to: to,
            channel: this.channelConfig.channelName + (this.channelConfig.channelNamespace ? `:${this.channelConfig.channelNamespace}` : ""),
            event: event,
            content: messageStr,
            ref_id: refId,
            thread_id: thread_id
        };
        this.websocketHandler.sendMessage(JSON.stringify(message));
        return ackPromise;
    }
    handleMessage(event, payload) { }
}
